import { QueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next/types';
import { getSession } from 'next-auth/react';
import { useEffect } from 'react';

import MainLoader from '../components/Loader';
import useSignIn, { toRelativeRedirect } from '../hooks/useSignIn';
import { provisionFlags } from '../utils/ServerSideFeatureFlagUtils';

// /signin is a pure redirect page — it must never send the user back to itself or
// to /login. Otherwise a missing/stripped Referer (direct visit) falls back to this
// page's own URL, and once signed in, /login's "already authenticated" redirect
// blindly honors whatever ?redirect= it was given — bouncing forever between
// /login and /signin.
const AUTH_BOUNCE_PATHS = new Set(['/signin', '/login']);
const safeDestination = (referer: string | null): string => {
  const relative = toRelativeRedirect(referer ?? undefined);
  if (!relative) return '/';
  return AUTH_BOUNCE_PATHS.has(relative.split('?')[0]) ? '/' : relative;
};

const Authenticated = ({ referer }: { referer: string | null }) => {
  const router = useRouter();

  useEffect(() => {
    router.push(safeDestination(referer));
  }, [referer, router]);
  return <>{<MainLoader />}</>;
};
const UnAuthenticated = ({ referer }: { referer: string | null }) => {
  const { signIn: doSignIn } = useSignIn();

  useEffect(() => {
    doSignIn({
      callbackUrl: safeDestination(referer),
      authParams: { ui_locales: 'en' },
    });
  }, [referer, doSignIn]);
  return <MainLoader />;
};

const SigninPage = ({
  authenticated,
  referer,
}: {
  authenticated: boolean;
  referer: string | null;
}) => {
  if (authenticated) return <Authenticated referer={referer} />;
  return <UnAuthenticated referer={referer} />;
};
export const getServerSideProps: GetServerSideProps = async context => {
  const session = await getSession({ req: context.req });
  context.res.setHeader(
    'Cache-Control',
    'no-store, no-cache, must-revalidate, proxy-revalidate',
  );
  const queryClient = new QueryClient();
  // Provisions US_AUTH (among others) server-side so the client Unleash instance
  // bootstraps with the correct value from the first frame — otherwise
  // useFeatureFlag('US_AUTH') inside useSignIn briefly reads the localStorage
  // default before its own async refresh, and this unauthenticated-only page could
  // route into legacy B2C on that first render.
  const flags = await provisionFlags(context, queryClient, session);
  return {
    props: {
      ...flags,
      authenticated: !!session?.user,
      referer: context.req.headers.referer ?? null,
    },
  };
};
export default SigninPage;
